import { useId, type ReactNode } from 'react';
import { cn } from '@/shared/lib/cn';

export interface SegmentedOption<TValue extends string> {
  value: TValue;
  label: ReactNode;
  icon?: ReactNode;
  disabled?: boolean;
}

export interface SegmentedControlProps<TValue extends string> {
  options: readonly SegmentedOption<TValue>[];
  value: TValue;
  onValueChange: (value: TValue) => void;
  /** Etykieta grupy dla czytnikow ekranu. */
  label: string;
  size?: 'sm' | 'md';
  /** Rozciaga segmenty na cala szerokosc. */
  fullWidth?: boolean;
  className?: string;
}

/**
 * Grupa wzajemnie wykluczajacych sie opcji w jednej obramowanej ramce —
 * „Open | Amator | Pro” z tworzenia turnieju oraz „Widok: Korty | Widok: Lista”
 * z ekranu rozmieszczenia.
 *
 * Rozni sie od `Tabs` przeznaczeniem: `Tabs` przelacza sekcje widoku,
 * `SegmentedControl` ustawia WARTOSC — dlatego uzywa roli `radiogroup`.
 */
export function SegmentedControl<TValue extends string>({
  options,
  value,
  onValueChange,
  label,
  size = 'md',
  fullWidth = false,
  className,
}: SegmentedControlProps<TValue>) {
  const groupId = useId();

  return (
    <div
      role="radiogroup"
      aria-label={label}
      className={cn(
        'border-border-strong bg-surface inline-flex rounded-lg border p-1',
        fullWidth && 'w-full',
        className,
      )}
    >
      {options.map((option) => {
        const isActive = option.value === value;

        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            id={`${groupId}-${option.value}`}
            aria-checked={isActive}
            disabled={option.disabled}
            onClick={() => onValueChange(option.value)}
            className={cn(
              'inline-flex items-center justify-center gap-2 font-semibold whitespace-nowrap',
              'rounded-md transition-colors duration-150',
              'disabled:pointer-events-none disabled:opacity-45',
              '[&_svg]:size-4',
              size === 'sm' ? 'h-7 px-3 text-xs' : 'h-9 px-4 text-[0.8125rem]',
              fullWidth && 'flex-1',
              isActive
                ? 'bg-brand text-fg-on-brand'
                : 'text-fg-muted hover:bg-surface-hover hover:text-fg',
            )}
          >
            {option.icon}
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
